export async function cgGet(path) {
  const r = await fetch(`https://api.coingecko.com/api/v3${path}`);
  if (!r.ok) throw new Error(`CoinGecko ${r.status}`);
  return r.json();
}

// ── 全市场交易数据聚合 ────────────────────────────────
// CoinGecko /coins/{id}/tickers 返回所有已索引的 trading pair（CEX + DEX 混合），
// 每个 ticker 包含 market.identifier + converted_volume.usd。
// 把这堆数据聚合成：top 5 交易所 / Binance 单列 / CEX vs DEX 占比 —— 给链上信号面板用。
//
// 用 /coins/{id}/tickers，**不要**用 /exchanges/{exchange}/tickers — 后者按交易所拉，
// 项目侧拉的是 token 视角，正合需求。
//
// DEX 识别：CoinGecko 不直接标 market_type，只能靠 identifier 关键字匹配 + has_trading_incentive。
// 已知主要 DEX identifier 来自 CG 数据观察。
const DEX_IDENTIFIERS = new Set([
  'uniswap_v3', 'uniswap_v2', 'uniswap-v3-arbitrum', 'uniswap-v3-polygon-pos',
  'pancakeswap_new', 'pancakeswap-v3', 'pancakeswap_v3_eth',
  'sushiswap', 'sushiswap_v3',
  'curve', 'curve-finance',
  'balancer_v2', 'balancer_v3',
  'cetus', 'turbos-finance',                       // Sui DEX
  'raydium', 'orca', 'jupiter-exchange-solana',    // Solana DEX
  'cake_dex',                                       // Aptos
  'trader-joe-v2', 'trader-joe',                    // Avalanche
  'dodo', 'kyberswap', 'maverick',
  'osmosis', 'astroport',                          // Cosmos
  'aerodrome-base', 'velodrome',                    // L2 DEX
  'gmx', 'hyperliquid',                             // Perp DEX
]);

function isDexMarket(market) {
  if (!market) return false;
  const id = (market.identifier || '').toLowerCase();
  if (DEX_IDENTIFIERS.has(id)) return true;
  // 主流 DEX 命名 pattern 兜底
  if (/uniswap|pancake|sushi|curve|balancer|raydium|orca|jupiter|aerodrome|velodrome|trader[-_]?joe|gmx|hyperliquid|kyber|maverick|dodo|osmosis|astroport|cetus|turbos/i.test(id)) return true;
  return false;
}

/**
 * 把 CoinGecko tickers 数组聚合成全市场交易切片。
 * 纯函数 — 已经 fetch 过 /coins/{id}?tickers=true 的调用方直接传 cgData.tickers 即可。
 *
 * @param {Array} tickers — CoinGecko tickers 数组（每个含 market + converted_volume.usd）
 * @returns {{
 *   totalVolume: number,        // 全市场 24h 聚合（USD）
 *   binanceVolume: number,      // Binance 单独 24h（USD）
 *   binanceShare: number,       // 0-1
 *   topExchanges: Array<{name, identifier, volume, share, isDex}>,  // top 5 by volume
 *   cexTotal: number,
 *   dexTotal: number,
 *   cexShare: number,           // 0-1
 *   tickerCount: number,
 * } | null}
 */
export function computeVolumeBreakdownFromTickers(tickers) {
  if (!Array.isArray(tickers) || tickers.length === 0) return null;

  const byExchange = new Map(); // identifier → { name, identifier, volume, isDex }
  let totalVolume = 0;
  let cexTotal = 0;
  let dexTotal = 0;
  let binanceVolume = 0;

  for (const t of tickers) {
    const vol = Number(t.converted_volume?.usd) || 0;
    if (vol <= 0) continue;
    const id = t.market?.identifier || 'unknown';
    const name = t.market?.name || id;
    const isDex = isDexMarket(t.market);
    const prev = byExchange.get(id) || { name, identifier: id, volume: 0, isDex };
    prev.volume += vol;
    byExchange.set(id, prev);
    totalVolume += vol;
    if (isDex) dexTotal += vol;
    else cexTotal += vol;
    if (id === 'binance') binanceVolume += vol;
  }

  if (totalVolume === 0) return null;

  const topExchanges = [...byExchange.values()]
    .sort((a, b) => b.volume - a.volume)
    .slice(0, 5)
    .map(e => ({ ...e, share: e.volume / totalVolume }));

  return {
    totalVolume,
    binanceVolume,
    binanceShare: binanceVolume / totalVolume,
    topExchanges,
    cexTotal,
    dexTotal,
    cexShare: cexTotal / totalVolume,
    tickerCount: tickers.length,
  };
}

/**
 * Fetch + 聚合 wrapper — 当调用方没有 cgData.tickers 时用这个。
 * @param {string} cgId — CoinGecko coin id（e.g. "sui"）
 */
export async function cgGetVolumeBreakdown(cgId) {
  if (!cgId) return null;
  try {
    const res = await cgGet(`/coins/${encodeURIComponent(cgId)}/tickers?include_exchange_logo=false`);
    return computeVolumeBreakdownFromTickers(res.tickers || []);
  } catch {
    return null;
  }
}

// ── 短符号防呆 ────────────────────────────────────────
// CoinGecko symbol 长度 ≤ 2 时（H/X/D/G/...）/search 返回结果普遍含多个
// 同名废弃币，单纯按 market_cap_rank 排序经常命中错的项目。
// 例：Humanity Protocol 的 symbol "H" 在 CG 上有 6+ 个匹配，
//     最旧的可能 rank 更靠前 → 拿到的不是真的 Humanity Protocol。
//
// 这里给个项目名相似度过滤：当 symbol 短时，优先选 name/id 与 project name
// 匹配的结果；如果都不匹配，宁可返回 null 也不蒙错。
const SHORT_SYMBOL_THRESHOLD = 2;

function normalizeForMatch(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function similarityScore(candidate, projectName) {
  if (!projectName) return 0;
  const c = normalizeForMatch(candidate);
  const p = normalizeForMatch(projectName);
  if (!c || !p) return 0;
  if (c === p) return 100;
  if (c.includes(p)) return 80;
  if (p.includes(c)) return 70;
  // 首词匹配（"humanity protocol" 的 "humanity" 进 candidate）
  const firstWord = p.split(/\s+/)[0];
  const firstWordNorm = normalizeForMatch(firstWord);
  if (firstWordNorm && firstWordNorm.length >= 3 && c.includes(firstWordNorm)) return 60;
  return 0;
}

// CoinGecko uses its own slug IDs (e.g. "ethereum" not "ETH").
// Search by symbol to find the correct ID.
// 对短符号（≤ 2 字符）+ 有 projectName 时强制按项目名匹配优先级。
export async function cgFindId(symbol, projectName = null) {
  if (!symbol) return null;
  const res = await cgGet(`/search?query=${encodeURIComponent(symbol)}`);
  let coins = (res.coins || []).filter(c => c.symbol.toLowerCase() === symbol.toLowerCase());
  if (!coins.length) return null;

  // ── 短符号防呆：必须通过项目名相似度过滤 ─────────────
  if (symbol.length <= SHORT_SYMBOL_THRESHOLD && projectName) {
    const scored = coins
      .map(c => ({ coin: c, score: Math.max(similarityScore(c.name, projectName), similarityScore(c.id, projectName)) }))
      .filter(x => x.score > 0); // 没相似度直接淘汰
    if (scored.length > 0) {
      // 高相似度优先，相同分数下按 market_cap_rank 排
      scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return (a.coin.market_cap_rank || 9999) - (b.coin.market_cap_rank || 9999);
      });
      return scored[0].coin.id;
    }
    // 短符号且没有任何 candidate 名字像 projectName：宁可放空，不蒙错
    // 例外：只有一个候选时还是返回，给上游一个机会
    if (coins.length === 1) return coins[0].id;
    return null;
  }

  // 普通流程（symbol 长 ≥ 3 或 无 projectName）：按 market cap 排
  coins.sort((a, b) => (a.market_cap_rank || 9999) - (b.market_cap_rank || 9999));

  // 如果有 projectName，作为软性 tiebreaker（不淘汰，只重排前 3）
  if (projectName && coins.length > 1) {
    const top3 = coins.slice(0, 3).map(c => ({
      coin: c,
      score: Math.max(similarityScore(c.name, projectName), similarityScore(c.id, projectName)),
    }));
    const bestSim = top3.find(x => x.score >= 60);
    if (bestSim) return bestSim.coin.id;
  }

  return coins[0].id;
}
